import { useEffect, useRef, useState } from "react";

import * as service from "../services/userPopsViewsService";

const MIN_WATCH_SECONDS = 3;

export const useUserPopsViews = () => {
  const [viewCounts, setViewCounts] = useState({});
  const [viewStats, setViewStats] = useState({});
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState("");

  const mountedRef = useRef(false);
  const recordedPopIdsRef = useRef(new Set());
  const recordingPopIdsRef = useRef(new Set());

  const loadedPopIdsRef = useRef(new Set());
  const fetchRequestsRef = useRef(new Map());
  const viewCountsRef = useRef({});

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const updateViewCount = (popId, views) => {
    const key = String(popId);
    const count = Number(views);

    if (!Number.isFinite(count) || count < 0) {
      return;
    }

    viewCountsRef.current = {
      ...viewCountsRef.current,
      [key]: count,
    };

    if (mountedRef.current) {
      setViewCounts(viewCountsRef.current);
    }
  };

  const fetchPopViewCount = async (popId, force = false) => {
    const id = Number(popId);

    if (!Number.isSafeInteger(id) || id <= 0) {
      return false;
    }

    const key = String(id);

    if (fetchRequestsRef.current.has(key)) {
      return fetchRequestsRef.current.get(key);
    }

    if (!force && loadedPopIdsRef.current.has(key)) {
      return viewCountsRef.current[key] ?? 0;
    }

    const countAtStart = viewCountsRef.current[key];

    const request = (async () => {
      try {
        const response = await service.getPopViewCount(id);

        if (response?.success !== true) {
          throw new Error(response?.message || "Unable to retrieve Pop views.");
        }

        const views = Number(response.totalViews ?? response.views);

        if (!Number.isFinite(views) || views < 0) {
          throw new Error("Invalid Pop view count.");
        }

        if (mountedRef.current) {
          loadedPopIdsRef.current.add(key);

          if (viewCountsRef.current[key] === countAtStart) {
            updateViewCount(id, views);
          }
        }

        return views;
      } catch (err) {
        console.error("fetchPopViewCount error:", err);

        return false;
      } finally {
        fetchRequestsRef.current.delete(key);
      }
    })();

    fetchRequestsRef.current.set(key, request);

    return request;
  };

  const recordPopView = async (
    popId,
    { watchSeconds = 0, completionPercent = 0, referrerType = "feed" } = {},
  ) => {
    const id = Number(popId);
    const seconds = Number(watchSeconds);
    const completion = Number(completionPercent);

    if (
      !Number.isSafeInteger(id) ||
      id <= 0 ||
      !Number.isFinite(seconds) ||
      seconds < MIN_WATCH_SECONDS ||
      !Number.isFinite(completion) ||
      completion < 0 ||
      completion > 100
    ) {
      return false;
    }

    const key = String(id);

    if (
      recordedPopIdsRef.current.has(key) ||
      recordingPopIdsRef.current.has(key)
    ) {
      return false;
    }

    recordingPopIdsRef.current.add(key);

    if (mountedRef.current) {
      setRecording(true);
      setError("");
    }

    try {
      const response = await service.recordPopView(id, {
        watchSeconds: Number(seconds.toFixed(2)),
        completionPercent: Number(completion.toFixed(2)),
        referrerType: String(referrerType || "feed"),
      });

      if (response?.success !== true) {
        throw new Error(response?.message || "Unable to record Pop view.");
      }

      recordedPopIdsRef.current.add(key);
      loadedPopIdsRef.current.add(key);

      const views = Number(response.totalViews ?? response.views);

      if (mountedRef.current && Number.isFinite(views) && views >= 0) {
        updateViewCount(id, views);

        setViewStats((previous) => ({
          ...previous,
          [key]: {
            realViews: Number(response.views) || 0,
            userViewCount: Number(response.userViewCount) || 0,
            totalWatchSeconds: Number(response.totalWatchSeconds) || 0,
            averageCompletion: Number(response.averageCompletion) || 0,
          },
        }));
      }

      return response;
    } catch (err) {
      console.error("recordPopView error:", err);

      if (mountedRef.current) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to record Pop view.",
        );
      }

      return false;
    } finally {
      recordingPopIdsRef.current.delete(key);

      if (mountedRef.current) {
        setRecording(recordingPopIdsRef.current.size > 0);
      }
    }
  };

  const getPopViewCount = (popId, fallback = 0) => {
    const key = String(popId);

    const count = viewCounts[key];

    if (Number.isFinite(count)) {
      return count;
    }

    const fallbackCount = Number(fallback);

    return Number.isFinite(fallbackCount) ? fallbackCount : 0;
  };

  const getPopViewStats = (popId) => {
    return viewStats[String(popId)] || null;
  };

  const getUserViewCount = (popId) => {
    return getPopViewStats(popId)?.userViewCount ?? 0;
  };

  const getTotalWatchSeconds = (popId) => {
    return getPopViewStats(popId)?.totalWatchSeconds ?? 0;
  };

  const getAverageCompletion = (popId) => {
    return getPopViewStats(popId)?.averageCompletion ?? 0;
  };

  const hasRecordedView = (popId) => {
    return recordedPopIdsRef.current.has(String(popId));
  };

  const isRecordingView = (popId) => {
    return recordingPopIdsRef.current.has(String(popId));
  };

  const resetRecordedViews = () => {
    recordedPopIdsRef.current.clear();
  };

  const getPopViewCounts = async (popIds = []) => {
    const ids = [
      ...new Set(
        popIds.map(Number).filter((id) => Number.isSafeInteger(id) && id > 0),
      ),
    ];

    if (!ids.length) return [];

    const countsAtStart = {};

    ids.forEach((id) => {
      countsAtStart[String(id)] = viewCountsRef.current[String(id)];
    });

    try {
      const response = await service.getPopViewCounts(ids);

      if (response?.success !== true) return [];

      const counts = {};

      for (const pop of response.pops || []) {
        const key = String(pop.popId);

        if (viewCountsRef.current[key] === countsAtStart[key]) {
          const views = Number(pop.views);

          if (Number.isFinite(views) && views >= 0) {
            counts[key] = views;
          }
        }
      }

      viewCountsRef.current = {
        ...viewCountsRef.current,
        ...counts,
      };

      if (mountedRef.current) {
        setViewCounts(viewCountsRef.current);
      }

      return response.pops;
    } catch (error) {
      console.error("getPopViewCounts failed:", error);
      return [];
    }
  };

  return {
    viewCounts,
    viewStats,
    recording,
    error,

    recordPopView,
    getPopViewCounts,
    getPopViewCount,

    fetchPopViewCount,
    getPopViewStats,
    getUserViewCount,
    getTotalWatchSeconds,
    getAverageCompletion,
    hasRecordedView,
    isRecordingView,
    resetRecordedViews,
  };
};

export default useUserPopsViews;
